import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableBody, TableCell, TableHead, TableRow, Paper
} from '@material-ui/core';
import { Query, Mutation } from 'react-apollo';
import {
  GET_CLIENT_DETAILS,
  GET_ALL_VEHICLES,
  GET_CALIBRATION_DETAILS,
  CONFIRM_VEHICLE_REGISTRATION
} from './queries';


// Mapping field keys to display labels
const fieldLabels = {
  vehicleNumber: 'Vehicle Number',
  solutionType: 'Solution Type',
  uniqueDeviceId: 'Device',
  litemode: 'Mode',
  planName: 'Plan Name',
  videoTelematicsType: 'Video Telematics Type',
  videoTelematicsTypeFromVehicleReg: 'Video Telematics',
  fuelType: "Fuel Type",
  vehicleMake: 'Vehicle Make',
  vehicleModel: 'Vehicle Model',
  noOfGears: 'No of Gears',
  gearRatio: 'Gear Ratio',
  isDualEngine: 'Dual Engine',
  hasArm: "Arm Included",
  isRotationSensor: "Rotation Sensor",
  solutionType: "Solution Type",
  hasSecondaryTank: 'Has Secondary Tank',
  availableDataTypes: 'Available Data Types',
  preferedDataType: 'Preferred Data Type',
};

const fuelSensorTypeLabels = {
  1: 'digital-sensor-data',
  2: 'analog-sensor-data',
  3: 'obd-data',
};

// Add a mapping for user-friendly preferred data type labels
const preferredDataTypeLabels = {
  1: 'Digital Sensor Data',
  2: 'Analog Sensor Data',
  3: 'OBD Data',
};

// Helper function to format videoTelematicsType
const formatVideoTelematicsType = (value) => {
  switch (value) {
    case 'loconav':
    case 'API-Loconav':
      return 'API-Loconav';
    case 'mettax':
    case 'API-Meta':
      return 'API-Meta';
    case 'lightMetrics':
    case 'iFrame-LM':
      return 'iFrame-LM';
    default:
      return value || 'NA';
  }
};
// Helper function to format videoTelematicsType
const formatVideoTelematicsTypeFromReg = (value) => {
  switch (value) {
    case 'loconav':
      return 'LocoNav';
    case 'mettax':
      return 'MettaX';
    default:
      return value || 'NA';
  }
};
const formatFuelType = (value) => {
  switch (value) {
    case 'petrol':
      return 'PETROL';
    case 'diesel':
      return 'DIESEL';
    case 'ev':
      return 'EV';
    case 'cng':
      return 'CNG';
    case 'lng':
      return 'LNG';
    default:
      return value || 'NA';
  }
};
const formatDualEngine = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'NA';
};
const formatSolutionType = (value) => {
  if (!value) return 'NA';
  
  // Custom mappings for vehicle categories/solution types
  const customMapping = {
    'fuel_solution': 'Fuel Solution',
    'obd_solution': 'OBD Solution',
    'obd_fuel+fuel_solution': 'OBD + Fuel Solution',
    'basic_tracking': 'Basic Tracking',
    'ais_140': 'AIS 140',
    'ais_140_obd_solution': 'AIS 140 OBD Solution',
    'ported_fuel_solution': 'Ported Fuel Solution',
    'control_tower': 'Control Tower',
    'testing_vehicle': 'Testing Vehicle',
    'dummy_vehicle': 'Dummy Vehicle',
    'video_telematics': 'Video Telematics',
    '3-Wheeler': '3-Wheeler',
    'Ambulance': 'Ambulance',
    'BIKE': 'Bike',
    'Boom Placer': 'Boom Placer',
    'CAR': 'Car',
    'Crane': 'Crane',
    'Dual-Engine': 'Dual-Engine',
    'Excavator': 'Excavator',
    'GeneratorTanker': 'GeneratorTanker',
    'Lift': 'Lift',
    'School Bus': 'School Bus',
    'Sixseater': 'Sixseater',
    'Stacker': 'Stacker',
    'Tow': 'Tow',
    'Tower Crane': 'Tower Crane',
    'Tractor': 'Tractor',
    'Truck': 'Truck',
    'Tug': 'Tug'
  };

  if (customMapping[value]) {
    return customMapping[value];
  }

  // Fallback: format snake_case or specific strings
  return value
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

// Component to preview vehicle details
const VehiclePreviewModal = ({ open, onClose, vehicleData, hasSecondaryTank, calibrationLoading, calibrationError, availableDataTypes, preferedDataType, isDualSensor, hasInSameTank, calibrationFilename, fuelSensorType, showConfirm = false, zone, keyAccountManager, onConfirm }) => {

  // Enhanced: Defensive check for vehicleData
  if (!vehicleData || typeof vehicleData !== 'object') {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Vehicle Registration Preview</DialogTitle>
        <DialogContent dividers>
          <Paper style={{ padding: 24, textAlign: 'center' }}>
            <b>No vehicle data available.</b>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const rows = Object.entries(vehicleData)
    .filter(([key]) => key !== 'videoTelematicsTypeFromVehicleReg' && key !== 'clientLoginId' && key !== 'fuelType' && key !== 'vehicleMake' && key !== 'vehicleModel' && key !== 'noOfGears' && key !== 'gearRatio' && key !== 'isDualEngine' && key !== 'hasArm' && key !== 'isRotationSensor' && key !== 'solutionType')
    .map(([key, value]) => ({
      label: fieldLabels[key] || key,
      value: value === undefined || value === null || value === '' ? 'NA' : String(value)
    }));

  const loginId = vehicleData.clientLoginId;
  console.log('loginId', loginId);

  // Map availableDataTypes array to labels
  let availableDataTypesDisplay = 'NA';
  if (Array.isArray(availableDataTypes) && availableDataTypes.length > 0) {
    availableDataTypesDisplay = availableDataTypes
      .map(type => fuelSensorTypeLabels[type] || String(type))
      .join(', ');
  }
  // Map preferedDataType to user-friendly label (single value)
  let preferedDataTypeDisplay = 'NA';
  if (typeof preferedDataType !== 'undefined' && preferedDataType !== null && preferedDataType !== '') {
    preferedDataTypeDisplay = preferredDataTypeLabels[preferedDataType] || String(preferedDataType);
  }

  // Map isDualSensor to Yes/No/NA
  let isDualSensorDisplay = 'NA';
  if (isDualSensor === true) isDualSensorDisplay = 'Yes';
  else if (isDualSensor === false) isDualSensorDisplay = 'No';

  // Map hasInSameTank to Yes/No/NA
  let hasInSameTankDisplay = 'NA';
  if (hasInSameTank === true) hasInSameTankDisplay = 'Yes';
  else if (hasInSameTank === false) hasInSameTankDisplay = 'No';

  // Map fuelSensorType to user-friendly label (string, e.g., 'Digital_sensor')
  let fuelSensorTypeDisplay = 'NA';
  if (typeof fuelSensorType === 'string' && fuelSensorType.trim() !== '') {
    fuelSensorTypeDisplay = fuelSensorType
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  } else if (typeof fuelSensorType !== 'undefined' && fuelSensorType !== null && fuelSensorType !== '') {
    fuelSensorTypeDisplay = String(fuelSensorType);
  }
  // Calibration filename display
  const calibrationFilenameDisplay = calibrationFilename || 'NA';

  // Helper to gather all data for mutation
  const getMutationInput = (clientData) => {
    // Get client details from the Query component's data
    const clientDetails = clientData && clientData.clientDetail ? clientData.clientDetail : {};
    console.log('clientDetails', clientDetails);
    const deviceDetails = vehicleData && vehicleData.deviceDetail ? vehicleData.deviceDetail : {};
    const planDetails = clientDetails && clientDetails.plan ? clientDetails.plan : {};

    return {
      // Basic vehicle data
      ...vehicleData,
      zone: clientDetails.zone || '',
      keyAccountManager: clientDetails.keyAccountManager || '',

      // Client details
      litemode: clientDetails.litemode,
      planName: planDetails.planName,
      videoTelematicsType: formatVideoTelematicsType(clientDetails.videoTelematicsType),
      videoTelematicsTypeFromVehicleReg: formatVideoTelematicsTypeFromReg(vehicleData.videoTelematicsTypeFromVehicleReg),

      // Vehicle specific details
      fuelType: formatFuelType(vehicleData.fuelType),
      vehicleMake: vehicleData.vehicleMake,
      vehicleModel: vehicleData.vehicleModel,
      noOfGears: vehicleData.noOfGears,
      gearRatio: vehicleData.gearRatio,
      isDualEngine: vehicleData.isDualEngine,
      hasArm: vehicleData.hasArm,
      isRotationSensor: vehicleData.isRotationSensor,
      solutionType: formatSolutionType(vehicleData.solutionType ? vehicleData.solutionType : ""),

      // Calibration details
      hasSecondaryTank,
      availableDataTypes,
      preferedDataType,
      isDualSensor,
      hasInSameTank,
      calibrationFilename,
      fuelSensorType,

      // Additional fields from vehicleData if they exist
      entityId: vehicleData.entityId,
      vehicleNumber: vehicleData.vehicleNumber,
      // deviceDetail: deviceDetails,
      uniqueDeviceId: vehicleData.uniqueDeviceId || '',

      // Collect all selected plans
      selectedPlans: Object.keys(vehicleData)
        .filter(key => key.startsWith('selected_plan_'))
        .reduce((acc, key) => {
          acc[key] = vehicleData[key];
          return acc;
        }, {})
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Vehicle Registration Preview</DialogTitle>
      <DialogContent dividers>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><b>Field</b></TableCell>
                <TableCell><b>Value</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} style={{ color: 'gray', textAlign: 'center' }}>No vehicle details to display.</TableCell>
                </TableRow>
              )}
              {rows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{row.value}</TableCell>
                </TableRow>
              ))}

              {loginId && (
                <Query
                  query={GET_CLIENT_DETAILS}
                  variables={{ loginId: parseInt(loginId) }}
                  fetchPolicy="network-only"
                >
                  {({ loading, error, data }) => {
                    if (loading)
                      return (
                        <TableRow>
                          <TableCell colSpan={2} style={{ color: 'gray' }}>Loading client details...</TableCell>
                        </TableRow>
                      );

                    if (error)
                      return (
                        <TableRow>
                          <TableCell colSpan={2} style={{ color: 'red' }}>Error loading client details: {error.message}</TableCell>
                        </TableRow>
                      );

                    if (!data || !data.clientDetail) {
                      return (
                        <TableRow>
                          <TableCell colSpan={2} style={{ color: 'gray' }}>Client details not found.</TableCell>
                        </TableRow>
                      );
                    }

                    return (
                      <React.Fragment>
                        <TableRow>
                          <TableCell>{fieldLabels['litemode']}</TableCell>
                          <TableCell>{data.clientDetail.litemode || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['planName']}</TableCell>
                          <TableCell>{data.clientDetail.plan && data.clientDetail.plan.planName ? data.clientDetail.plan.planName : 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['videoTelematicsType']}</TableCell>
                          <TableCell>{formatVideoTelematicsType(data.clientDetail.videoTelematicsType) || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['videoTelematicsTypeFromVehicleReg']}</TableCell>
                          <TableCell>{formatVideoTelematicsTypeFromReg(vehicleData.videoTelematicsTypeFromVehicleReg) || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['fuelType']}</TableCell>
                          <TableCell>{formatFuelType(vehicleData.fuelType) || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['vehicleMake']}</TableCell>
                          <TableCell>{vehicleData.vehicleMake || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['vehicleModel']}</TableCell>
                          <TableCell>{vehicleData.vehicleModel || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['noOfGears']}</TableCell>
                          <TableCell>{vehicleData.noOfGears || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['gearRatio']}</TableCell>
                          <TableCell>{vehicleData.gearRatio || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['isDualEngine']}</TableCell>
                          <TableCell>{formatDualEngine(vehicleData.isDualEngine) || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['hasArm']}</TableCell>
                          <TableCell>{formatDualEngine(vehicleData.hasArm) || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['isRotationSensor']}</TableCell>
                          <TableCell>{formatDualEngine(vehicleData.isRotationSensor) || 'NA'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{fieldLabels['solutionType']}</TableCell>
                          <TableCell>{formatSolutionType(vehicleData.solutionType) || 'NA'}</TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  }}
                </Query>
              )}

              {/* Calibration Details Row */}
              {calibrationLoading && (
                <TableRow>
                  <TableCell colSpan={2} style={{ color: 'gray' }}>Loading Calibration Details...</TableCell>
                </TableRow>
              )}
              {calibrationError && (
                <TableRow>
                  <TableCell colSpan={2} style={{ color: 'red' }}>Error: {typeof calibrationError === 'string' ? calibrationError : (calibrationError && calibrationError.message) || 'Unknown error'}</TableCell>
                </TableRow>
              )}
              {calibrationLoading === false && !calibrationError && (
                <React.Fragment>
                  <TableRow>
                    <TableCell>{fieldLabels['hasSecondaryTank']}</TableCell>
                    <TableCell>{hasSecondaryTank === null || typeof hasSecondaryTank === 'undefined' ? 'NA' : hasSecondaryTank === true ? 'Yes' : hasSecondaryTank === false ? 'No' : String(hasSecondaryTank)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{fieldLabels['availableDataTypes']}</TableCell>
                    <TableCell>{availableDataTypesDisplay}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{fieldLabels['preferedDataType']}</TableCell>
                    <TableCell>{preferedDataTypeDisplay}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Dual Sensor Available</TableCell>
                    <TableCell>{isDualSensorDisplay}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Is in Same Tank</TableCell>
                    <TableCell>{hasInSameTankDisplay}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Calibration Filename</TableCell>
                    <TableCell>{calibrationFilenameDisplay}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Fuel Sensor Type</TableCell>
                    <TableCell>{fuelSensorTypeDisplay}</TableCell>
                  </TableRow>
                </React.Fragment>
              )}
            </TableBody>
          </Table>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
        <Query
          query={GET_CLIENT_DETAILS}
          variables={{ loginId: vehicleData && vehicleData.clientLoginId ? parseInt(vehicleData.clientLoginId) : null }}
          fetchPolicy="network-only"
        >
          {({ loading: clientLoading, error: clientError, data: clientData }) => {

            if (clientLoading) {
              return <Button color="secondary" variant="contained" disabled>Loading...</Button>;
            }
            if (clientError) {
              return <span style={{ color: 'red', marginLeft: 16 }}>Error loading client details: {clientError.message}</span>;
            }
            if (!clientData || !clientData.clientDetail) {

              return <span style={{ color: 'gray', marginLeft: 16 }}>Client details not found.</span>;
            }
            return (
              <Button
                onClick={() => {
                  // Get the mutation input data
                  const mutationInput = getMutationInput(clientData);
                  if (onConfirm) onConfirm(mutationInput);
                }}
                color="secondary"
                variant="contained"
                disabled={clientLoading}
              >
                Confirm
              </Button>
            );
          }}
        </Query>
      </DialogActions>
    </Dialog>
  );
};

export default VehiclePreviewModal;



// WEBPACK FOOTER //
// ./src/components/Modules/VehicleRegistartion/VehiclePreviewModal.jsx